import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { splitSheetsAPI } from '../services/api';
import HelpGuide from '../components/HelpGuide';

interface Collaborator {
  id: number;
  name: string;
  email: string;
  role: string;
  percentage: number;
  signed_at: string | null;
  signature_image?: string | null;
}

interface SplitSheet {
  id: number;
  song_title: string;
  song_details: string;
  creator_id: number;
  status: string;
  s3_url: string | null;
  created_at: string;
  collaborators: Collaborator[];
}

const SplitSheetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [splitSheet, setSplitSheet] = useState<SplitSheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSignModal, setShowSignModal] = useState(false);
  const [selectedCollaborator, setSelectedCollaborator] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchSplitSheet = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      const response = await splitSheetsAPI.getOne(id);
      setSplitSheet(response.data.splitSheet);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch split sheet');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSplitSheet();
  }, [fetchSplitSheet]);

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    setError('');
    try {
      const response = await splitSheetsAPI.getPDF(id!);
      if (response.data.pdfUrl) {
      window.open(response.data.pdfUrl, '_blank');
      } else {
        setError('PDF URL not available');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          'Failed to generate PDF';
      setError(errorMessage);
    } finally {
      setPdfLoading(false);
    }
  };

  const allCollaboratorsSigned = () => {
    if (!splitSheet || !splitSheet.collaborators || splitSheet.collaborators.length === 0) {
      return false;
    }
    return splitSheet.collaborators.every(c => c.signed_at !== null);
  };

  const signedCount = () => {
    if (!splitSheet || !splitSheet.collaborators) return 0;
    return splitSheet.collaborators.filter(c => c.signed_at !== null).length;
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this split sheet?')) {
      try {
        await splitSheetsAPI.delete(id!);
        navigate('/dashboard');
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to delete split sheet');
      }
    }
  };

  const openSignModal = (collaboratorId: number) => {
    setSelectedCollaborator(collaboratorId);
    setShowSignModal(true);
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      signed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    
    return (
      <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !splitSheet) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error || 'Split sheet not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-4 sm:mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-primary-600 hover:text-primary-800 flex items-center text-sm"
        >
          <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline">Back to Dashboard</span>
          <span className="sm:hidden">Back</span>
        </button>
      </div>
      <div className="mb-4 sm:mb-6">
        <HelpGuide
          title="How to finish this split sheet"
          intro="Use this checklist to move from draft to final PDF."
          steps={[
            { title: 'Track pending signatures', detail: 'Look for collaborators marked Pending.' },
            { title: 'Capture each signature', detail: 'Use the Sign button for each pending collaborator.' },
            { title: 'Download final PDF', detail: 'Once everyone signs, use Download PDF at the top.' },
          ]}
          tone={allCollaboratorsSigned() ? 'success' : 'info'}
        />
      </div>

      {/* Header */}
      <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">{splitSheet.song_title}</h1>
            {splitSheet.song_details && (
              <p className="mt-2 text-sm sm:text-base text-gray-600 break-words">{splitSheet.song_details}</p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2 sm:gap-4">
              {getStatusBadge(splitSheet.status)}
              <span className="text-xs sm:text-sm text-gray-500">
                Created {new Date(splitSheet.created_at).toLocaleDateString('en-ZA')}
              </span>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:flex-shrink-0">
            <button
              onClick={handleDownloadPDF}
              disabled={pdfLoading || !allCollaboratorsSigned()}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pdfLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="hidden sm:inline">Generating PDF...</span>
                  <span className="sm:hidden">Generating...</span>
                </>
              ) : (
                <>
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="hidden sm:inline">{allCollaboratorsSigned() ? 'Download PDF' : 'PDF (Waiting for signatures)'}</span>
                  <span className="sm:hidden">{allCollaboratorsSigned() ? 'PDF' : 'PDF'}</span>
                </>
              )}
            </button>
            <button
              onClick={handleDelete}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* All Signed Message */}
      {allCollaboratorsSigned() && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="h-6 w-6 text-green-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-green-900">All Collaborators Have Signed!</h3>
              <p className="text-sm text-green-700 mt-1">
                The split sheet is complete. You can now download the final PDF document.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Signature Progress */}
      {!allCollaboratorsSigned() && splitSheet.collaborators && splitSheet.collaborators.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-900">
                Signing Progress: {signedCount()} of {splitSheet.collaborators.length} collaborators have signed
              </p>
            </div>
            <div className="flex-1 mx-4 bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(signedCount() / splitSheet.collaborators.length) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Collaborators */}
      <div className="bg-white shadow rounded-lg p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Collaborators & Splits</h2>
        
        {splitSheet.collaborators && splitSheet.collaborators.length > 0 ? (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {splitSheet.collaborators.map((collaborator) => (
                <div key={collaborator.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">{collaborator.name}</h3>
                      <p className="text-sm text-gray-500 mt-1 truncate">{collaborator.email}</p>
                    </div>
                    <div className="ml-2">
                      {collaborator.signed_at ? (
                        <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <span className="px-2 inline-flex items-center text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100">
                    <div>
                      <p className="text-xs text-gray-500">Role</p>
                      <p className="text-sm font-medium text-gray-900">{collaborator.role}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Percentage</p>
                      <p className="text-sm font-semibold text-gray-900">{collaborator.percentage}%</p>
                    </div>
                  </div>

                  {collaborator.signed_at && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">Signed on</p>
                      <p className="text-sm text-gray-900">{new Date(collaborator.signed_at).toLocaleDateString('en-ZA')}</p>
                      {collaborator.signature_image && (
                        <img 
                          src={collaborator.signature_image} 
                          alt={`${collaborator.name}'s signature`}
                          className="mt-2 h-8 w-24 border border-gray-300 rounded object-contain bg-white"
                        />
                      )}
                    </div>
                  )}

                  {!collaborator.signed_at && (
                    <div className="mt-3">
                      <button
                        onClick={() => openSignModal(collaborator.id)}
                        className="w-full text-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
                      >
                        Sign Document
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Percentage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                  {splitSheet.collaborators.map((collaborator) => (
                <tr key={collaborator.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {collaborator.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {collaborator.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {collaborator.role}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                    {collaborator.percentage}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {collaborator.signed_at ? (
                          <div className="flex items-center space-x-2">
                            <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="flex flex-col">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Signed {new Date(collaborator.signed_at).toLocaleDateString('en-ZA')}
                      </span>
                              {collaborator.signature_image && (
                                <img 
                                  src={collaborator.signature_image} 
                                  alt={`${collaborator.name}'s signature`}
                                  className="mt-1 h-8 w-24 border border-gray-300 rounded object-contain bg-white"
                                />
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {!collaborator.signed_at && (
                      <button
                        onClick={() => openSignModal(collaborator.id)}
                        className="text-primary-600 hover:text-primary-900 font-medium"
                      >
                        Sign
                      </button>
                    )}
                  </td>
                </tr>
                  ))}
            </tbody>
          </table>
        </div>
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No collaborators found
          </div>
        )}

        <div className="mt-4 p-4 bg-gray-50 rounded-md">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">Total Percentage:</span>
            <span className="text-lg font-bold text-gray-900">
              {(() => {
                if (!splitSheet.collaborators || splitSheet.collaborators.length === 0) {
                  return '0.00';
                }
                const total = splitSheet.collaborators.reduce((sum, c) => {
                  const percentage = parseFloat(String(c.percentage || 0));
                  return sum + (isNaN(percentage) ? 0 : percentage);
                }, 0);
                return isNaN(total) ? '0.00' : total.toFixed(2);
              })()}%
            </span>
          </div>
        </div>
      </div>

      {/* Sign Modal */}
      {showSignModal && (
        <SignatureModal
          splitSheetId={splitSheet.id}
          collaboratorId={selectedCollaborator!}
          onClose={() => {
            setShowSignModal(false);
            setSelectedCollaborator(null);
            fetchSplitSheet();
          }}
        />
      )}
    </div>
  );
};

// Signature Modal Component
interface SignatureModalProps {
  splitSheetId: number;
  collaboratorId: number;
  onClose: () => void;
}

const SignatureModal: React.FC<SignatureModalProps> = ({ splitSheetId, collaboratorId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const getEventPos = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    } else if ('clientX' in e) {
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    }
    return { x: 0, y: 0 };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const pos = getEventPos(e);
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
      }
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const pos = getEventPos(e);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
      }
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  };

  const handleSign = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Convert canvas to base64 data URL format: "data:image/png;base64,..."
    // This format is accepted by the backend API
    const dataUrl = canvas.toDataURL('image/png');
    
    // Verify signature data is not empty
    if (!dataUrl || dataUrl === 'data:,') {
      setError('Please draw your signature before submitting');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Send signature in data URL format (data:image/png;base64,...)
      // Backend accepts both data URL format and plain base64 string
      await splitSheetsAPI.sign(String(splitSheetId), {
        signatureData: dataUrl, // Format: "data:image/png;base64,iVBORw0KGgo..."
        collaboratorId
      });
      onClose();
    } catch (err: any) {
      console.error('Signature error:', err);
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          'Failed to sign split sheet';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-4 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-t-lg sm:rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 w-full">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Sign Split Sheet
            </h3>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            <p className="text-sm text-gray-600 mb-4">
              Draw your signature below. This will be legally binding.
            </p>

            <div className="border-2 border-gray-300 rounded-md mb-4 bg-white">
              <canvas
                ref={canvasRef}
                width={600}
                height={200}
                className="w-full h-48 sm:h-52 cursor-crosshair touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                onTouchCancel={stopDrawing}
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
              <button
                type="button"
                onClick={clearSignature}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clear
              </button>

              <div className="flex gap-3 sm:space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSign}
                  disabled={loading}
                  className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {loading ? 'Signing...' : 'Sign Document'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SplitSheetDetailPage;

